import React from 'react';
import PropTypes from 'prop-types';
import { FiUsers, FiUserCheck, FiSlash } from 'react-icons/fi';
import { Badge } from 'components/common';

// Mirrors Oauth::Application::IMPERSONATION_MODES — the API refuses anything else.
export const ANY_USER = 'any_user';
export const ALLOWLIST = 'allowlist';

// Mirrors Integration::ImpersonationPolicyService::MAX_ALLOWLIST_SIZE, so the
// cap is reported while typing rather than as a 422 after Save.
export const MAX_ALLOWLIST_SIZE = 200;

export const IMPERSONATION_MODES = [
  {
    value: ANY_USER,
    icon: FiUsers,
    title: 'Any user',
    caption: 'The SSO shape',
    description:
      'The client may be handed a token for anybody in the staff directory. Correct for integrations that swap the signed-in person for a portal token (Doc Search, DocBot, HR Hub, Meeting Room) — its client secret is then the only thing standing in the way.',
  },
  {
    value: ALLOWLIST,
    icon: FiUserCheck,
    title: 'Restricted allowlist',
    caption: 'The machine shape',
    description:
      'The client may only be handed a token for the addresses listed below, and nothing else. Correct for an integration that acts as one service account, such as the Opla webhook.',
  },
];

// Same order every render, so a saved list and a locally edited one compare cleanly.
export const normalizeEmails = (emails) =>
  [...new Set((emails || []).map((email) => String(email).toLowerCase().trim()).filter(Boolean))].sort();

export const sameEmails = (a, b) => a.length === b.length && a.every((email, i) => email === b[i]);

/**
 * What this client's secret can actually do if it leaks, in the one phrase the
 * list column, the identity card and the Overview tab all show — so "any user"
 * never reads as a tighter setting than it is, and an empty allowlist reads as
 * the refusal it is.
 */
const ImpersonationBadge = ({ record, size = 'sm', deniedLabel = 'Denied' }) => {
  // A record that has not loaded yet has no mode and no list, which would read
  // as "denied" — the one direction that matters, because it makes a wide-open
  // client look bounded. Say nothing instead.
  if (!record?.impersonation_mode) return <span className="tw-text-slate-300">—</span>;

  if (record.impersonation_mode === ANY_USER) {
    return <Badge variant="warning" size={size}><FiUsers size={11} /> Any user</Badge>;
  }

  const count = record.impersonatable_emails?.length || 0;
  if (count === 0) return <Badge variant="danger" size={size}><FiSlash size={11} /> {deniedLabel}</Badge>;

  return <Badge variant="success" size={size}><FiUserCheck size={11} /> {count} allowed</Badge>;
};

ImpersonationBadge.propTypes = {
  record: PropTypes.shape({
    impersonation_mode: PropTypes.string,
    impersonatable_emails: PropTypes.arrayOf(PropTypes.string),
  }),
  size: PropTypes.string,
  deniedLabel: PropTypes.string,
};

export default ImpersonationBadge;
